import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  CheckCircle,
  Circle,
  AlertCircle,
  Plus,
  MapPin,
  Flag,
  X,
  Paperclip,
  File,
} from "lucide-react";
import useTask from "../../hooks/useTask";
import useCalendar from "../../hooks/useCalendar";
import TaskModal from "./TaskModal";
import FileSelectorModal from "./FileSelectorModal";
import AlertModal from "./AlertModal";

/**
 * 대시보드 컴포넌트
 * 오늘의 할 일 목록을 표시
 */
export default function Dashboard() {
  const { fetchTodayTodo, changeTaskStatus, createTask, fetchTaskDetail } =
    useTask();
  const { fetchMyCalendars } = useCalendar();

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  const [calendars, setCalendars] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [todoDetail, setTodoDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    loadTodos();
    loadCalendars();
  }, []);

  const loadCalendars = async () => {
    const result = await fetchMyCalendars();
    if (result.ok) {
      setCalendars(result.calendars);
    }
  };

  const loadTodos = async () => {
    setLoading(true);
    const result = await fetchTodayTodo();
    if (result.ok) {
      setTodos(result.todos);
    }
    setLoading(false);
  };

  const handleStatusChange = async (todo) => {
    const newStatus = todo.status === "completed" ? "pending" : "completed";
    const result = await changeTaskStatus({
      calendar_id: todo.calendar_id,
      task_id: todo.id,
      status: newStatus,
    });

    if (result.ok) {
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t))
      );
    } else if (result.viewOnly) {
      alert("읽기 권한만 있어 상태를 변경할 수 없습니다.");
    }
  };

  const handleCreateTask = async (taskData) => {
    const result = await createTask({
      ...taskData,
      task_date: new Date().toISOString().split("T")[0], // 오늘 날짜로 설정
      attendee_files: selectedFiles.map((f) => f.id),
    });

    if (result.ok) {
      setShowTaskModal(false);
      setSelectedFiles([]);
      loadTodos();
      setAlertConfig({
        title: "성공",
        message: "일정이 추가되었습니다.",
        type: "success",
      });
      setShowAlert(true);
    } else if (result.viewOnly) {
      setAlertConfig({
        title: "권한 없음",
        message: "일정을 추가할 권한이 없습니다.",
        type: "error",
      });
      setShowAlert(true);
    }
  };

  const handleFileSelect = (files) => {
    setSelectedFiles(files);
    setShowFileModal(false);
  };

  const getTimeDisplay = (todo) => {
    if (todo.is_all_day) return "하루 종일";
    if (todo.start_time) {
      // 시간 문자열에서 HH:MM 부분만 추출
      const extractTime = (timeStr) => {
        if (!timeStr) return "";
        // "HH:MM:SS" 형식이거나 날짜가 포함된 경우 처리
        const timePart = timeStr.split("T")[1] || timeStr;
        return timePart.substring(0, 5);
      };

      const start = extractTime(todo.start_time);
      const end = todo.end_time ? extractTime(todo.end_time) : "";
      return end ? `${start} - ${end}` : start;
    }
    return "";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "text-red-500";
      case "medium":
        return "text-yellow-500";
      case "low":
        return "text-gray-500";
      default:
        return "text-gray-500";
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      work: "업무",
      meeting: "회의",
      deadline: "마감일",
      personal: "개인",
      other: "기타",
    };
    return labels[category] || category;
  };

  // ✅ 상세 보기 클릭 핸들러 (상단에 떨어져 있던 걸 정위치로 이동)
  const handleTodoClick = async (todo) => {
    setSelectedTodo(todo);
    setShowDetailModal(true);
    setLoadingDetail(true);

    // 상세 정보 조회
    const result = await fetchTaskDetail({
      task_id: todo.id,
      is_virtual_event: todo.event_type === "recurring_virtual",
      recurring_event_id: todo.recurring_event_id || "",
      date: todo.task_date,
    });

    if (result.ok) {
      setTodoDetail(result.detail);
    }
    setLoadingDetail(false);
  };

  // ✅ 내부 모달 컴포넌트 (부모 상태/함수 접근 위해 내부로 이동)
  function TaskDetailModal({ todo, isOpen, onClose }) {
    if (!isOpen || !todo) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            {/* 헤더 */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {todo.title}
                </h3>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span
                    className={`font-medium ${getPriorityColor(todo.priority)}`}
                  >
                    {todo.priority === "high"
                      ? "높음"
                      : todo.priority === "medium"
                      ? "보통"
                      : "낮음"}
                  </span>
                  <span className="bg-gray-100 px-2 py-1 rounded">
                    {getCategoryLabel(todo.category)}
                  </span>
                  <span
                    className={`px-2 py-1 rounded ${
                      todo.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {todo.status === "completed" ? "완료됨" : "진행 중"}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 상세 정보 */}
            <div className="space-y-4">
              {/* 시간 */}
              <div className="flex items-center text-gray-700">
                <Clock className="w-5 h-5 mr-3 text-gray-400" />
                <span>{getTimeDisplay(todo)}</span>
              </div>

              {/* 위치 */}
              {todo.location && (
                <div className="flex items-center text-gray-700">
                  <MapPin className="w-5 h-5 mr-3 text-gray-400" />
                  <span>{todo.location}</span>
                </div>
              )}

              {/* 캘린더 */}
              <div className="flex items-center text-gray-700">
                <Calendar className="w-5 h-5 mr-3 text-gray-400" />
                <span>
                  {calendars.find((cal) => cal.id === todo.calendar_id)
                    ?.calendar_name || "캘린더"}
                </span>
              </div>

              {/* 설명 */}
              {todo.description && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">설명</h4>
                  <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                    {todo.description}
                  </p>
                </div>
              )}

              {/* 첨부파일 */}
              {loadingDetail ? (
                <div className="text-sm text-gray-500">로딩 중...</div>
              ) : todoDetail?.attachments &&
                todoDetail.attachments.length > 0 ? (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                    <Paperclip className="w-4 h-4 mr-1" />
                    첨부파일 ({todoDetail.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {todoDetail.attachments.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                      >
                        <File className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm text-gray-700 flex-1">
                          {file.file_name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {(file.file_size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* 생성/수정 시간 */}
              <div className="text-sm text-gray-500 pt-4 border-t">
                <p>생성: {new Date(todo.created_at).toLocaleString("ko-KR")}</p>
                {todo.updated_at && (
                  <p>
                    수정: {new Date(todo.updated_at).toLocaleString("ko-KR")}
                  </p>
                )}
                {todo.completed_at && (
                  <p>
                    완료: {new Date(todo.completed_at).toLocaleString("ko-KR")}
                  </p>
                )}
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="flex gap-2 mt-6">
              {todo.status === "pending" ? (
                <button
                  onClick={() => {
                    handleStatusChange(todo);
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  완료로 표시
                </button>
              ) : (
                <button
                  onClick={() => {
                    handleStatusChange(todo);
                    onClose();
                  }}
                  className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  진행 중으로 변경
                </button>
              )}
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pendingTodos = todos.filter((t) => t.status === "pending");
  const completedTodos = todos.filter((t) => t.status === "completed");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">오늘의 할 일</h1>
        <p className="text-gray-600">
          {new Date().toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
          })}
        </p>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">전체 일정</p>
              <p className="text-2xl font-bold">{todos.length}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">진행 중</p>
              <p className="text-2xl font-bold">{pendingTodos.length}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">완료됨</p>
              <p className="text-2xl font-bold">{completedTodos.length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* 할 일 목록 */}
      <div className="space-y-4">
        {/* 진행 중인 일정 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold flex items-center">
              <Circle className="w-5 h-5 text-yellow-500 mr-2" />
              진행 중 ({pendingTodos.length})
            </h2>
          </div>
          <div className="divide-y">
            {pendingTodos.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                진행 중인 일정이 없습니다
              </div>
            ) : (
              pendingTodos.map((todo) => (
                <div
                  key={todo.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleTodoClick(todo)}
                >
                  <div className="flex items-start">
                    <div className="mr-3 mt-1">
                      <Circle className="w-5 h-5 text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-medium text-gray-800">
                          {todo.title}
                        </h3>
                        <span
                          className={`text-sm font-medium ${getPriorityColor(
                            todo.priority
                          )}`}
                        >
                          {todo.priority === "high"
                            ? "높음"
                            : todo.priority === "medium"
                            ? "보통"
                            : "낮음"}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 space-x-4">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {getTimeDisplay(todo)}
                        </span>
                        <span className="bg-gray-100 px-2 py-1 rounded">
                          {getCategoryLabel(todo.category)}
                        </span>
                        {todo.location && <span>{todo.location}</span>}
                      </div>
                      {todo.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                          {todo.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 완료된 일정 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              완료됨 ({completedTodos.length})
            </h2>
          </div>
          <div className="divide-y">
            {completedTodos.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                완료된 일정이 없습니다
              </div>
            ) : (
              completedTodos.map((todo) => (
                <div
                  key={todo.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleTodoClick(todo)}
                >
                  <div className="flex items-start">
                    <div className="mr-3 mt-1">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1 opacity-60">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-medium text-gray-800 line-through">
                          {todo.title}
                        </h3>
                        <span
                          className={`text-sm font-medium ${getPriorityColor(
                            todo.priority
                          )}`}
                        >
                          {todo.priority === "high"
                            ? "높음"
                            : todo.priority === "medium"
                            ? "보통"
                            : "낮음"}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 space-x-4">
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {getTimeDisplay(todo)}
                        </span>
                        <span className="bg-gray-100 px-2 py-1 rounded">
                          {getCategoryLabel(todo.category)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* 빈 상태 */}
      {todos.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">
            오늘 일정이 없습니다
          </h3>
          <p className="text-gray-600">캘린더에서 새로운 일정을 추가해보세요</p>
        </div>
      )}

      {/* 플로팅 버튼 */}
      <button
        onClick={() => setShowTaskModal(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
        title="새 일정 추가"
      >
        <Plus className="w-6 h-6" />
        <span className="absolute right-full mr-3 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          새 일정 추가
        </span>
      </button>

      {/* Task Modal */}
      <TaskModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setSelectedFiles([]);
        }}
        onSave={handleCreateTask}
        calendarId={null} // null로 설정하여 사용자가 선택하도록 함
        onFileSelect={() => setShowFileModal(true)}
        selectedFiles={selectedFiles}
      />

      {/* File Selector Modal */}
      <FileSelectorModal
        isOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        onSelect={handleFileSelect}
        multiple={true}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        {...alertConfig}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        todo={selectedTodo}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedTodo(null);
          setTodoDetail(null);
        }}
      />
    </div>
  );
}
